import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    FiInbox,
    FiSearch,
    FiArrowLeft,
    FiArrowUpRight,
} from "react-icons/fi";
import {
    PageHeader,
    StatusBadge,
    EmptyState,
    SkeletonTableRow,
} from "../../theme/components";
import { PageButton } from "../../theme/components/PageButton";
import { contactApi } from "../../api/endpoints/contact";
import { formatDateTimeDisplay } from "../../utils/dates";
import "./AdminHelpMessages.css";

const STATUS_OPTIONS = [
    { value: "all", label: "All" },
    { value: "new", label: "New" },
    { value: "read", label: "Read" },
    { value: "resolved", label: "Resolved" },
];

const STATUS_SEVERITY = {
    new: "info",
    read: "warning",
    resolved: "success",
};

const formatDate = (iso) => formatDateTimeDisplay(iso);

const COLUMNS = [
    { key: "date", label: "Date", width: "150px" },
    { key: "from", label: "From" },
    { key: "topic", label: "Topic", width: "160px" },
    { key: "status", label: "Status", width: "120px" },
    { key: "message", label: "Message" },
    { key: "actions", label: "", width: "60px" },
];

const AdminHelpMessages = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const statusFromUrl = searchParams.get("status") || "all";
    const [status, setStatus] = useState(statusFromUrl);
    const [query, setQuery] = useState("");
    const [messages, setMessages] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const list =
                    status === "all"
                        ? await contactApi.list()
                        : await contactApi.list(status);
                if (cancelled) return;
                setMessages(list);
            } catch (e) {
                if (cancelled) return;
                setError(
                    e?.response?.data?.message || e?.message || "Failed to load messages"
                );
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [status]);

    const setStatusAndUrl = (next) => {
        setStatus(next);
        if (next === "all") {
            searchParams.delete("status");
        } else {
            searchParams.set("status", next);
        }
        setSearchParams(searchParams, { replace: true });
    };

    const filtered = useMemo(() => {
        if (!messages) return [];
        const q = query.trim().toLowerCase();
        if (!q) return messages;
        return messages.filter((m) => {
            return (
                (m.userEmail || "").toLowerCase().includes(q) ||
                (m.userName || "").toLowerCase().includes(q) ||
                (m.message || "").toLowerCase().includes(q) ||
                (m.topic || "").toLowerCase().includes(q) ||
                (m.customReason || "").toLowerCase().includes(q)
            );
        });
    }, [messages, query]);

    return (
        <div className="admin-messages-wrapper">
            <PageHeader
                title="Help Messages"
                subtitle="Triage and respond to user-submitted feedback"
                icon={FiInbox}
                actions={
                    <PageButton
                        type="button"
                        variant="ghost"
                        startIcon={<FiArrowLeft size={16} />}
                        onClick={() => navigate("/admin")}
                    >
                        Admin
                    </PageButton>
                }
            />

            <div className="admin-messages-body">
                <div className="admin-messages-filters">
                    <div className="admin-messages-pills">
                        {STATUS_OPTIONS.map((opt) => {
                            const active = status === opt.value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`admin-pill ${active ? "is-active" : ""}`}
                                    onClick={() => setStatusAndUrl(opt.value)}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                    <div className="admin-messages-search">
                        <FiSearch size={14} />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search email, message, topic…"
                            aria-label="Search messages"
                        />
                    </div>
                </div>

                <div className="admin-messages-card">
                    <div className="admin-messages-table-wrap">
                        <table className="admin-messages-table">
                            <thead>
                                <tr>
                                    {COLUMNS.map((c) => (
                                        <th key={c.key} style={{ width: c.width }}>
                                            {c.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {messages === null ? (
                                    Array.from({ length: 6 }).map((_, i) => (
                                        <SkeletonTableRow key={i} cols={COLUMNS.length} />
                                    ))
                                ) : error ? (
                                    <tr>
                                        <td colSpan={COLUMNS.length} className="admin-messages-empty">
                                            <EmptyState
                                                illustration="error"
                                                compact
                                                title="Couldn't load messages"
                                                description={error}
                                            />
                                        </td>
                                    </tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={COLUMNS.length} className="admin-messages-empty">
                                            <EmptyState
                                                illustration={query ? "search" : "no-data"}
                                                compact
                                                title={
                                                    query
                                                        ? "No messages match your search"
                                                        : status === "all"
                                                        ? "No help messages yet"
                                                        : `No messages with status "${status}"`
                                                }
                                            />
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((m) => (
                                        <tr
                                            key={m.id}
                                            className="admin-messages-row"
                                            onClick={() =>
                                                navigate(`/admin/messages/${m.id}`)
                                            }
                                        >
                                            <td>{formatDate(m.createdAt)}</td>
                                            <td>
                                                <div className="admin-messages-from">
                                                    <span className="admin-messages-email">
                                                        {m.userEmail}
                                                    </span>
                                                    {m.userName && m.userName !== m.userEmail && (
                                                        <span className="admin-messages-name">
                                                            {m.userName}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="admin-messages-topic">
                                                    <span className="admin-messages-topic-label">
                                                        {m.topic}
                                                    </span>
                                                    {m.customReason && (
                                                        <span
                                                            className="admin-messages-topic-reason"
                                                            title={m.customReason}
                                                        >
                                                            {m.customReason}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <StatusBadge
                                                    severity={STATUS_SEVERITY[m.status] || "neutral"}
                                                >
                                                    {m.status}
                                                </StatusBadge>
                                            </td>
                                            <td>
                                                <span className="admin-messages-snippet">
                                                    {m.message}
                                                </span>
                                            </td>
                                            <td>
                                                <FiArrowUpRight
                                                    size={16}
                                                    className="admin-messages-open"
                                                />
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminHelpMessages;
